import React, { useEffect, useState } from "react";
import Button from "@components/buttons/Button/Button";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import GuestBookPDFViewer from "@components/misc/GuestBookPDF/GuestBookPDFViewer";
import GuestBookPDFDownloadButton from "@components/misc/GuestBookPDF/GuestBookPDFDownloadButton";
import { Orientation, PageSize } from "@components/misc/GuestBookPDF/GuestBookDocument";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { Memorial } from "@models/Memorial";
import { fetchMemorial } from "@services/memorialService";
import { GuestBook, GuestBookEntry } from "@models/GuestBook";
import { Business } from "@models/Business";
import { fetchBusiness } from "@services/businessService";

const GuestBookPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const exampleBusinessId = searchParams.get("exampleBusinessId"); // For example only

    const [memorial, setMemorial] = useState<Memorial>();
    const [business, setBusiness] = useState<Business>();
    const [orientation, setOrientation] = useState<Orientation>("portrait");
    const [pageSize, setPageSize] = useState<PageSize>("A5");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch services on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (id) {
                    const data = await fetchMemorial(id);
                    if (data.businessId) {
                        const fetchedBusiness = await fetchBusiness(data.businessId);
                        setBusiness(fetchedBusiness);
                    }
                    setMemorial(data);
                } else if (exampleBusinessId) {
                    const fetchedBusiness = await fetchBusiness(exampleBusinessId);
                    setBusiness(fetchedBusiness);

                    const EXAMPLE_GUEST_BOOK_DATA: GuestBookEntry[] = [
                        { id: "1", name: "Emily Clarke", timestamp: new Date("2025-03-30T10:15:00Z"), message: "Sending love and prayers." },
                        { id: "2", name: "James", timestamp: new Date("2025-03-30T10:18:22Z"), message: "You’ll always be remembered with love." },
                        { id: "3", name: "Ava Martin", timestamp: new Date("2025-03-30T10:21:45Z"), message: "Rest in peace. You'll be deeply missed." },
                        { id: "4", name: "Liam", timestamp: new Date("2025-03-30T10:23:30Z"), message: "Gone but never forgotten." },
                        { id: "5", name: "Sophia Nguyen", timestamp: new Date("2025-03-30T10:27:10Z"), message: "Forever in our hearts." },
                        { id: "6", name: "Noah", timestamp: new Date("2025-03-30T10:29:00Z"), message: "Thinking of you and your family." },
                        { id: "7", name: "Olivia Chen", timestamp: new Date("2025-03-30T10:31:15Z"), message: "May your soul rest in peace." },
                        { id: "8", name: "William", timestamp: new Date("2025-03-30T10:34:42Z"), message: "With deepest sympathy." },
                        { id: "9", name: "Mia Thompson", timestamp: new Date("2025-03-30T10:36:58Z"), message: "Thank you for the memories." },
                        { id: "10", name: "Benjamin", timestamp: new Date("2025-03-30T10:39:21Z"), message: "A beautiful soul, never forgotten." },
                        { id: "11", name: "Isabella Lopez", timestamp: new Date("2025-03-30T10:41:00Z"), message: "Wishing peace and comfort to your loved ones." },
                        { id: "12", name: "Lucas", timestamp: new Date("2025-03-30T10:43:17Z"), message: "May you rest in eternal peace." },
                        { id: "13", name: "Ella Rodriguez", timestamp: new Date("2025-03-30T10:46:00Z"), message: "Grateful for the memories we shared." },
                        { id: "14", name: "Henry", timestamp: new Date("2025-03-30T10:49:03Z"), message: "You left behind a legacy of love." },
                        { id: "15", name: "Amelia Green", timestamp: new Date("2025-03-30T10:52:16Z"), message: "Your kindness will never be forgotten." },
                        { id: "16", name: "Jack", timestamp: new Date("2025-03-30T10:54:45Z"), message: "Rest easy, friend." },
                        { id: "17", name: "Grace Patel", timestamp: new Date("2025-03-30T10:57:10Z"), message: "You touched so many lives." },
                        { id: "18", name: "Elijah", timestamp: new Date("2025-03-30T11:00:33Z"), message: "In loving memory." },
                        { id: "19", name: "Chloe Brooks", timestamp: new Date("2025-03-30T11:02:48Z"), message: "You brought joy to so many." },
                        { id: "20", name: "Logan", timestamp: new Date("2025-03-30T11:06:10Z"), message: "Rest in peace and power." },
                    ];

                    const EXAMPLE_MEMORIAL: Memorial = {
                        id: "1",
                        firstName: "Aldo",
                        lastName: "Crapis",
                        birthDate: new Date("1966-11-09"),
                        deathDate: new Date("2024-02-24"),
                        image: "/assets/demo/image.jpg",
                        serviceDateTime: new Date("2024-03-11T11:00:00.000+00:00"),
                        serviceLocation: "Our Lady of Good Counsel Church",
                        obituary: "",
                        timeline: [],
                        gallery: [],
                        tributes: [],
                        guestBook: EXAMPLE_GUEST_BOOK_DATA,
                        pageAdminEmail: "",
                        setAdminStatus: "not set",
                        businessId: exampleBusinessId,
                        previewKey: "",
                        qrKey: "",
                        status: "active",
                        privacy: {
                            published: true,
                            noIndex: false,
                            guestBookOpen: false,
                            tributesOpen: false,
                            emailOnTribute: false,
                            restrictPostContentToQr: false,
                            restrictViewInfoToQr: false,
                            restrictViewTimelineGalleryToQr: false,
                            restrictViewTributesToQr: false,
                        },
                    };
                    setMemorial(EXAMPLE_MEMORIAL);
                } else {
                    throw new Error("No ID.");
                }
            } catch (err: unknown) {
                setError("Failed to fetch guest book.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [exampleBusinessId, id]);

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (!memorial) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>No memorial</div>;
        }

        const guestBook: GuestBook = {
            firstName: memorial.firstName,
            lastName: memorial.lastName,
            birthDate: memorial.birthDate,
            deathDate: memorial.deathDate,
            image: memorial.image || "/assets/images/placeholder-person.png",
            serviceDateTime: memorial.serviceDateTime,
            serviceLocation: memorial.serviceLocation,
            businessLogo: business?.logo || "/assets/images/fioh-text-logo.png",
            entries: memorial.guestBook,
        };

        return (
            <div className={commonStyles.container}>
                <div className={commonStyles.headerContainer}>
                    <h1>{`${memorial?.firstName} ${memorial?.lastName} - Guest Book`}</h1>
                    <div className={commonStyles.buttonGroup}>
                        <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => navigate(`../guest-book-moderation?${new URLSearchParams({ id: id || "" }).toString()}`)}
                        >
                            Moderate
                        </Button>
                        <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => setOrientation((prevState) => (
                                prevState === "landscape" ? "portrait" : "landscape"
                            ))}
                        >
                            {orientation === "landscape" ? "Portrait" : "Landscape"}
                        </Button>
                        <Button
                            variant="secondary"
                            size="medium"
                            onClick={() => setPageSize((prev) => (prev === "A4" ? "A5" : "A4"))}
                        >
                            {`Change to ${pageSize === "A4" ? "A5" : "A4"}`}
                        </Button>
                        <GuestBookPDFDownloadButton
                            guestBook={guestBook}
                            orientation={orientation}
                            pageSize={pageSize}
                            overrideName={exampleBusinessId ? `${business?.name}_GuestBook` : undefined}
                        />
                    </div>
                </div>
                <GuestBookPDFViewer
                    guestBook={guestBook}
                    orientation={orientation}
                    pageSize={pageSize}
                />
            </div>
        );
    };

    return renderContent();
};

export default GuestBookPage;
